import {
  memo,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Input,
  Button,
  Spinner,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { ChevronDown20Regular, Dismiss20Regular } from '@fluentui/react-icons';
import { getVirtualWindow } from './virtualWindow';

export interface ISearchableDropdownOption {
  key: string;
  label: string;
}

export interface SearchableDropdownProps {
  options?: ISearchableDropdownOption[];
  loadOptions?: (query: string) => Promise<ISearchableDropdownOption[]>;
  debounceMs?: number;
  value?: string;
  valueLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  onSelect: (key: string, option: ISearchableDropdownOption) => void;
  onClear?: () => void;
  maxListHeight?: number;
  className?: string;
}

const ROW_HEIGHT = 36;

const useStyles = makeStyles({
  root: {
    position: 'relative',
    width: '100%',
  },
  input: {
    width: '100%',
    maxWidth: '100%',
  },
  list: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    zIndex: 1,
    overflowY: 'auto',
    overflowAnchor: 'none',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow8,
  },
  spacer: {
    position: 'relative',
    width: '100%',
  },
  empty: {
    display: 'block',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
  },
  option: {
    appearance: 'none',
    background: 'transparent',
    border: 'none',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    margin: 0,
    padding: `0 ${tokens.spacingHorizontalM}`,
    overflow: 'hidden',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    cursor: 'pointer',
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '-2px',
    },
  },
  optionActive: {
    backgroundColor: tokens.colorNeutralBackground1Hover,
  },
  optionSelected: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
    fontWeight: tokens.fontWeightSemibold,
  },
});

type OptionRowProps = {
  option: ISearchableDropdownOption;
  index: number;
  id: string;
  top: number;
  selected: boolean;
  className: string;
  onSelect: (option: ISearchableDropdownOption) => void;
  onActive: (index: number) => void;
};

const OptionRow = memo(function OptionRow({
  option,
  index,
  id,
  top,
  selected,
  className,
  onSelect,
  onActive,
}: OptionRowProps) {
  return (
    <button
      id={id}
      type="button"
      role="option"
      aria-selected={selected}
      className={className}
      style={{
        position: 'absolute',
        top,
        left: 0,
        right: 0,
        height: ROW_HEIGHT,
      }}
      onMouseDown={(event) => event.preventDefault()}
      onMouseEnter={() => onActive(index)}
      onClick={() => onSelect(option)}
    >
      {option.label}
    </button>
  );
});

export const SearchableDropdown = ({
  options,
  loadOptions,
  debounceMs = 200,
  value,
  valueLabel,
  placeholder,
  disabled,
  onSelect,
  onClear,
  maxListHeight = 220,
  className,
}: SearchableDropdownProps) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const didScrollOnOpen = useRef(false);
  const reqIdRef = useRef(0);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const [asyncOptions, setAsyncOptions] = useState<ISearchableDropdownOption[]>(
    []
  );
  const [loading, setLoading] = useState(false);

  const isAsync = typeof loadOptions === 'function';

  useEffect(() => {
    if (!query.trim()) {
      setDebouncedQuery('');
      return;
    }
    const timer = setTimeout(() => setDebouncedQuery(query), debounceMs);
    return () => clearTimeout(timer);
  }, [query, debounceMs]);

  useEffect(() => {
    if (!isAsync || !open) return;
    const requestId = ++reqIdRef.current;
    setLoading(true);
    void loadOptions!(debouncedQuery)
      .then((results) => {
        if (requestId !== reqIdRef.current) return;
        setAsyncOptions(results);
      })
      .catch(() => {
        if (requestId !== reqIdRef.current) return;
        setAsyncOptions([]);
      })
      .finally(() => {
        if (requestId === reqIdRef.current) setLoading(false);
      });
    return () => {
      reqIdRef.current += 1;
    };
  }, [isAsync, open, debouncedQuery, loadOptions]);

  const selectedLabel = useMemo(() => {
    if (valueLabel != null) return valueLabel;
    const source = isAsync ? asyncOptions : options ?? [];
    return source.find((option) => option.key === value)?.label ?? '';
  }, [isAsync, asyncOptions, options, value, valueLabel]);

  const filtered = useMemo(() => {
    if (isAsync) return asyncOptions;
    const list = options ?? [];
    const needle = debouncedQuery.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((option) => option.label.toLowerCase().includes(needle));
  }, [isAsync, asyncOptions, options, debouncedQuery]);

  const viewportHeight = Math.min(
    maxListHeight,
    Math.max(ROW_HEIGHT, filtered.length * ROW_HEIGHT)
  );
  const virtualWindow = getVirtualWindow(
    filtered.length,
    ROW_HEIGHT,
    scrollTop,
    viewportHeight
  );

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setDebouncedQuery('');
    setScrollTop(0);
    setHighlightedIndex(0);
    didScrollOnOpen.current = false;
  }, []);

  const handleSelect = useCallback(
    (option: ISearchableDropdownOption) => {
      close();
      inputRef.current?.blur();
      onSelect(option.key, option);
    },
    [close, onSelect]
  );

  const handleClear = () => {
    onClear?.();
    setQuery('');
    setDebouncedQuery('');
    inputRef.current?.focus();
  };

  const setActive = useCallback((index: number) => {
    setHighlightedIndex(index);
  }, []);

  const scrollIndexIntoView = (index: number) => {
    const list = listRef.current;
    if (!list || index < 0) return;
    const top = index * ROW_HEIGHT;
    const bottom = top + ROW_HEIGHT;
    if (top < list.scrollTop) {
      list.scrollTop = top;
    } else if (bottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = bottom - list.clientHeight;
    }
    setScrollTop(list.scrollTop);
  };

  const openList = () => {
    if (disabled || open) return;
    setOpen(true);
    setQuery('');
    setDebouncedQuery('');
    setScrollTop(0);
  };

  useEffect(() => {
    if (!open) {
      didScrollOnOpen.current = false;
      return;
    }
    if (loading || didScrollOnOpen.current || debouncedQuery.trim()) return;
    if (!filtered.length) return;
    const selected = filtered.findIndex((option) => option.key === value);
    const index = selected >= 0 ? selected : 0;
    didScrollOnOpen.current = true;
    setHighlightedIndex(index);
    const frame = requestAnimationFrame(() => {
      const list = listRef.current;
      if (!list || selected < 0) return;
      list.scrollTop = Math.max(
        0,
        index * ROW_HEIGHT - (list.clientHeight - ROW_HEIGHT) / 2
      );
      setScrollTop(list.scrollTop);
    });
    return () => cancelAnimationFrame(frame);
  }, [open, loading, debouncedQuery, filtered, value]);

  useEffect(() => {
    if (!open || !debouncedQuery.trim()) return;
    setHighlightedIndex(0);
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
    setScrollTop(0);
  }, [debouncedQuery, open]);

  const moveHighlight = (nextIndex: number) => {
    if (!filtered.length) return;
    const index = Math.min(filtered.length - 1, Math.max(0, nextIndex));
    setHighlightedIndex(index);
    scrollIndexIntoView(index);
  };

  const activeOptionId =
    open && filtered[highlightedIndex]
      ? `${listId}-opt-${highlightedIndex}`
      : undefined;

  return (
    <div
      className={mergeClasses(styles.root, className)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          close();
        }
      }}
    >
      <Input
        ref={inputRef}
        className={styles.input}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={activeOptionId}
        aria-autocomplete="list"
        placeholder={placeholder}
        value={open ? query : selectedLabel}
        disabled={disabled}
        onChange={(_, data) => {
          setQuery(data.value);
          setOpen(true);
        }}
        onClick={openList}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            close();
            inputRef.current?.blur();
            return;
          }
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (!open) {
              openList();
              return;
            }
            moveHighlight(highlightedIndex + 1);
            return;
          }
          if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (!open) {
              openList();
              return;
            }
            moveHighlight(highlightedIndex - 1);
            return;
          }
          if (event.key === 'Home' && open) {
            event.preventDefault();
            moveHighlight(0);
            return;
          }
          if (event.key === 'End' && open) {
            event.preventDefault();
            moveHighlight(filtered.length - 1);
            return;
          }
          if (event.key === 'Enter' && open) {
            event.preventDefault();
            const option = filtered[highlightedIndex];
            if (option) handleSelect(option);
          }
        }}
        contentAfter={
          <>
            {onClear && value && (
              <Button
                appearance="transparent"
                size="small"
                icon={<Dismiss20Regular />}
                aria-label={t('clear', 'Clear')}
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleClear}
              />
            )}
            <Button
              appearance="transparent"
              size="small"
              icon={<ChevronDown20Regular />}
              aria-label={open ? t('close', 'Close') : t('open', 'Open')}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                if (disabled) return;
                if (open) close();
                else {
                  openList();
                  inputRef.current?.focus();
                }
              }}
            />
          </>
        }
      />
      {open && (
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          className={styles.list}
          style={{
            height:
              loading || filtered.length === 0 ? undefined : viewportHeight,
          }}
          onMouseDown={(event) => event.preventDefault()}
          onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
        >
          {loading ? (
            <div className={styles.loading}>
              <Spinner size="tiny" />
            </div>
          ) : filtered.length === 0 ? (
            <span className={styles.empty}>{t('noMatches', 'No matches')}</span>
          ) : (
            <div
              className={styles.spacer}
              style={{ height: virtualWindow.totalHeight }}
            >
              {filtered
                .slice(virtualWindow.start, virtualWindow.end)
                .map((option, offset) => {
                  const index = virtualWindow.start + offset;
                  return (
                    <OptionRow
                      key={option.key}
                      option={option}
                      index={index}
                      id={`${listId}-opt-${index}`}
                      top={index * ROW_HEIGHT}
                      selected={option.key === value}
                      className={mergeClasses(
                        styles.option,
                        index === highlightedIndex && styles.optionActive,
                        option.key === value && styles.optionSelected
                      )}
                      onSelect={handleSelect}
                      onActive={setActive}
                    />
                  );
                })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
